import {sortWith} from 'ramda';

import {EmployeeWarehouseAccess, EmployeeWarehouseAccessRange} from '@dms/api/customer';
import {GetWarehousesResponse} from '@dms/api/warehouse';

import {Nullish} from 'shared/web';

export type WarehouseAccessRange = EmployeeWarehouseAccessRange | 'NONE';

export type WarehouseAccessRow = {
  warehouseId: string;
  warehouseNumber: string | Nullish;
  warehouseName: string | Nullish;
  accessRange: WarehouseAccessRange;
};

/**
 * @about Merge the tenant's warehouses with the employee's warehouse access.
 * Warehouses are sorted by name, with numbers in names compared numerically ("Sklad 9" before "Sklad 10").
 * A warehouse without a stored row is unassigned (`NONE`). A stored row whose warehouse
 * is missing from the list (a deleted warehouse) is appended after the sorted warehouses,
 * so the admin can still see it and set it to `NONE`.
 */
export const getWarehouseAccessRows = (
  warehouses: GetWarehousesResponse,
  warehouseAccess: EmployeeWarehouseAccess[]
): WarehouseAccessRow[] => {
  const accessRangeByWarehouseId = new Map(
    warehouseAccess.map((access) => [access.warehouseId, access.accessRange])
  );
  const warehouseIds = new Set(warehouses.map((warehouse) => warehouse.id));

  const warehouseRows = sortWith(
    [
      (first, second) =>
        (first.name ?? '').localeCompare(second.name ?? '', undefined, {numeric: true}),
    ],
    warehouses
  ).map(
    (warehouse): WarehouseAccessRow => ({
      warehouseId: warehouse.id,
      warehouseNumber: warehouse.warehouseNumber,
      warehouseName: warehouse.name,
      accessRange: accessRangeByWarehouseId.get(warehouse.id) ?? 'NONE',
    })
  );

  const unknownWarehouseRows = warehouseAccess
    .filter((access) => !warehouseIds.has(access.warehouseId))
    .map((access) => ({
      warehouseId: access.warehouseId,
      warehouseNumber: access.warehouseNumber,
      warehouseName: access.warehouseName,
      accessRange: access.accessRange,
    }));

  return [...warehouseRows, ...unknownWarehouseRows];
};
